import type { FetchiosError } from "@caprikorn/dynamo-utils";
import { type QueryObserverOptions, useQuery } from "@tanstack/react-query";

import { useGroupReferences } from "@contexts/GroupReferences/GroupReferences.context";
import { apiEcmsHttp } from "@services/http";
import { QUERY_KEYS } from "@services/keys";

import type {
  GetExpositionPoint,
  GetExpositionPointParams,
} from "./apiEcms.types";

const getExpositionPoint = async (
  {
    customerId,
    contractId,
    siteId,
    readingPage,
    elementsPerPage,
  }: GetExpositionPointParams,
  signal: AbortSignal,
) => {
  const { data } = await apiEcmsHttp.get<GetExpositionPoint>(
    "/exposition-point",
    {
      params: {
        customerId,
        contractId,
        siteId,
        readingPage,
        elementsPerPage,
      },
      signal,
    },
  );

  return data;
};

export const useGetExpositionPoint = <SelectType = GetExpositionPoint>(
  {
    customerId,
    contractId,
    siteId,
    readingPage = 1,
    elementsPerPage,
  }: GetExpositionPointParams,
  options?: Partial<
    QueryObserverOptions<GetExpositionPoint, FetchiosError, SelectType>
  >,
) => {
  const { currentGroup } = useGroupReferences();

  return useQuery<GetExpositionPoint, FetchiosError, SelectType>({
    queryKey: [
      QUERY_KEYS.EXPOSITION_POINT,
      customerId,
      contractId,
      siteId,
      currentGroup,
      readingPage,
      elementsPerPage,
    ],
    queryFn: ({ signal }) =>
      getExpositionPoint(
        {
          customerId,
          contractId,
          siteId,
          readingPage,
          elementsPerPage,
        },
        signal,
      ),
    ...options,
  });
};
